/**
 * Where a tap on a CV-preview section goes during onboarding.
 *
 * The `/cv` route sends these to `/(app)/cv/edit-*` routes, but those live
 * behind the onboarding-complete guard and get their sheet presentation from
 * the CV layout, so onboarding cannot reuse them. It maps each section to the
 * onboarding step that already edits it instead; those steps are off the linear
 * flow, so `resolveNextOnboardingStep` returns them to the preview on save.
 *
 * Shared by `StepComplete.tsx` and `StepComplete.web.tsx` so the two platforms
 * can never disagree about where a section tap lands.
 */
import type { OnboardingStep } from './OnboardingProvider';

export type CvPreviewEditSection =
  | 'edit-header'
  | 'edit-summary'
  | 'edit-highlights'
  | 'edit-skills'
  | 'edit-experience'
  | 'edit-education'
  | 'edit-certifications'
  | 'edit-languages';

export const PREVIEW_EDIT_STEP: Record<CvPreviewEditSection, OnboardingStep> = {
  // The generic header target owns headline and contact details. The avatar is
  // a distinct target below because identity/photo belong to this-is-you.
  'edit-header': 'how-to-reach-you',
  'edit-summary': 'summary',
  'edit-highlights': 'highlights',
  'edit-skills': 'skills',
  'edit-experience': 'experience',
  'edit-education': 'education',
  'edit-certifications': 'certifications',
  'edit-languages': 'languages',
};

export const PREVIEW_PHOTO_EDIT_STEP: OnboardingStep = 'this-is-you';

export function resolvePreviewEditStep(
  section: CvPreviewEditSection,
): OnboardingStep {
  return PREVIEW_EDIT_STEP[section];
}

export function resolvePreviewPhotoEditStep(): OnboardingStep {
  return PREVIEW_PHOTO_EDIT_STEP;
}
